import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Divider } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { BsBoxSeam, BsCreditCard, BsReceipt } from "react-icons/bs";
import { FaRegUser } from "react-icons/fa";
import Header from "../../Components/Common/Header";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import OrderList from "./OrderList";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import {
  STATE_CULTURE,
  STATE_CURRENCY,
  STATE_POS_ID,
} from "../../Actions/cookiesActions";
import axios from "axios";
import ReceiptActions from "./ReceiptActions";
import ContentLayout from "../../Components/Styles/ContentLayout";
import { ADD_CULTURES, AppContext } from "../../context";
import { KLARNA } from "../../Components/Payments/constants";
import KlarnaReceipt from "../../Components/Receipt/klarnaReceipt";
import TagManager from "../../Components/TagManager";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const query = gql`
  query receiptQuery($saleId: Guid, $culture: String) {
    receipt(saleId: $saleId, culture: $culture) {
      reference
      culture
      discount
      discountId
      posId
      shopUri
      receiptNumber
      saleDate
      saleId
      sumExclVat
      sumInclVat
      sumExclVatRaw
      sumInclVatRaw
      ticketsUri
      hasDownloadableTickets
      barcodeUri
      walletUri
      giftCertificates {
        uri
        article {
          name
        }
      }
      articles {
        id
        plu
        name
        quantity
        displayPriceExclVat
        displayPriceInclVat
        displayPriceExclVatRaw
        displayPriceInclVatRaw
        displayPricePerPieceInclVatRaw
        info
        isFee
      }
      payments {
        id
        amount
        currency {
          name
        }
        paymentMethodName
        paymentMethodId
        transactionInfo
      }
      vat {
        id
        amount
        amountRaw
        vatFormula
      }
      customer {
        id
        email
        firstName
        lastName
        companyName
      }
    }
  }
`;

const appQuery = gql`
  query AppQuery {
    cultures {
      name
      description
    }
  }
`;

const useStyles = createStyles(() => ({
  newCheckout: {
    display: "grid",
    gridTemplateColumns: "0.7fr 0.3fr",
    gridGap: "2rem",
    width: "100%",
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "1rem",
    "@media screen and (max-width: 950px)": {
      gridTemplateColumns: "1fr",
      padding: 0,
    },
    "@media print": {
      gridTemplateColumns: "1fr",
    },
  },
  informationWrapper: {
    backgroundColor: "#fff",
    padding: "1.5rem 1.75rem",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    "@media screen and (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      padding: "1rem",
    },
  },
  label: {
    fontWeight: 500,
  },
  actions: {
    display: "flex",
    gap: "1rem",
  },
  detailWrapper: {
    display: "flex",
    justifyContent: "start",
    gap: "2rem",
    paddingBottom: "2rem",
    "& ul": {
      listStyle: "none",
      margin: 0,
      padding: 0,
    },
    "@media screen and (max-width: 768px)": {
      flexDirection: "column",
    },
  },
  detail: {
    display: "flex",
    flexDirection: "column",
    "& span": {
      margin: 0,
      fontWeight: 500,
    },
  },
  iconWrapper: {
    display: "flex",
    justifyContent: "start",
    alignItems: "start",
    "& svg": {
      marginTop: "0.30rem",
      marginRight: "0.5rem",
    },
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: ".25rem",
    padding: 0,
    marginTop: "-1rem",
    listStyle: "none",
  },
  text: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.875rem",
    color: "#323232",
    margin: 0,
    wordBreak: "break-word",
  },
  footerWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  footerBold: {
    fontWeight: 500,
  },
  total: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "1.125rem",
    fontWeight: "700",
    margin: "1rem 0",
  },
  title: {
    marginTop: "0.3rem",
    marginBottom: "1rem",
  },
  headingWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    button: {
      color: "var(--link-color)",
      padding: "0.5rem 0.5rem",
      "&:hover": {
        backgroundColor: "var(--button-primary-hover-bg-color)",
      },
    },
    "@media screen and (max-width: 768px)": {
      flexDirection: "column-reverse",
    },
  },
}));

let source;

const getQueryVariable = (variable) => {
  const query = window.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    let pair = vars[i].split("=");
    if (pair[0] === variable) {
      return pair[1];
    }
  }

  return null;
};

const NewReceipt = (props) => {
  const [text, setText] = useState(null);
  const [useKlarnaReceipt, setUseKlarnaReceipt] = useState(false);
  const { config, posId, culture, currency, language } = useSelector(
    (state) => ({
      config: state.config,
      posId: state.cookies[STATE_POS_ID],
      currency: state.cookies[STATE_CURRENCY],
      culture: state.cookies[STATE_CULTURE],
      language: state.language,
    })
  );
  const { state, dispatch } = useContext(AppContext);
  const { classes } = useStyles();
  const { id: saleId } = useParams();

  const { data } = useQuery(query, {
    variables: { saleId, posId, culture },
    fetchPolicy: "no-cache",
  });
  const { data: appData } = useQuery(appQuery, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (config && language) {
      document.title = `${language.Receipt} - ${config.shopName}`;
    }
  }, [config, language]);

  useEffect(() => {
    if (appData) {
      dispatch({ type: ADD_CULTURES, payload: appData.cultures });
    }
  }, [appData]);

  useEffect(() => {
    // Removes saleId from cookies/localStorage

    localStorage.removeItem("saleId");
    document.cookie =
      "checkout-saleId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie =
      "shop-saleId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }, []);

  useEffect(() => {
    let receiptPayments = data?.receipt?.payments;
    if (!receiptPayments) return;

    let klarnaPayment = receiptPayments.find(
      (p) => p.paymentMethodId === KLARNA
    );
    let useProviderReceipt = getQueryVariable("upr");
    if (
      useProviderReceipt != null &&
      useProviderReceipt === "1" &&
      klarnaPayment
    ) {
      setUseKlarnaReceipt(true);
    }
  }, [data]);

  useEffect(() => {
    let CancelToken = axios.CancelToken;
    source = CancelToken.source();
    axios
      .get(
        `${config.baseUrl}/ee-static/checkout/pos-${posId}/Texts/receipt.${culture}.md`,
        {
          cancelToken: source.token,
        }
      )
      .then((r) => setText(r.data))
      .catch((e) => console.error(e));

    return () => source.cancel("Component Is Unmounting");
  }, [culture, posId]);

  const getCurrencySum = (sum) => {
    switch (currency) {
      case "USD":
        return `$${sum}`;
      case "EUR":
        return `€${sum}`;
      case "SEK":
        return `${sum} kr`;
      case "DKK":
        return `${sum} kr`;
      case "NOK":
        return `${sum} kr`;
      default:
        return `${sum} kr`;
    }
  };

  const fee = useCallback(() => {
    const filter = data?.receipt.articles.filter((article) => article.isFee);
    if (filter?.length > 0) {
      return filter.map((el, i) => (
        <div key={i} className={classes.footerWrapper}>
          <span className={classes.text}>{el.name}</span>
          <span className={classes.footerBold}>{el.displayPriceInclVat}</span>
        </div>
      ));
    } else {
      return null;
    }
  }, [data]);

  const goBackUrl = localStorage.getItem("goBackUrl");
  const isBooking =
    goBackUrl &&
    (goBackUrl.includes("booking") || goBackUrl.includes("membersapp"));
  const isShop = goBackUrl && goBackUrl.includes("ticketshop");

  const navigateBack = () => {
    const referrerPathname = document.referrer.split("/")?.[3];
    if (goBackUrl) {
      window.location.href = goBackUrl;
    } else if (referrerPathname === "") {
      window.location = `${document.referrer}ticketshop`;
    } else {
      window.location = document.referrer;
    }
  };

  if (!data) return null;
  return (
    <ContentLayout>
      {data && data?.receipt?.posId && (
        <TagManager posId={data?.receipt?.posId} receipt={data?.receipt} />
      )}
      <Header
        config={config}
        hideUser={true}
        shopUrl={config.shopUri}
        language={language}
      />
      <div className={classes.newCheckout}>
        <section className={classes.informationWrapper} id="main-page-content">
          <Button
            className="button-subtle"
            style={{ padding: "0.2rem" }}
            variant="subtle"
            onClick={navigateBack}
          >
            {language && isShop && language.Back_To_Shop}
            {language && isBooking && language.Back_To_Booking}
          </Button>
          <div className={classes.headingWrapper}>
            <h1 className={classes.title}>{language.ThanksForTheOrder}</h1>
          </div>
          {text && (
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{text}</ReactMarkdown>
          )}
          <div className={classes.actions}>
            <ReceiptActions
              hideMobileTicket={config?.hideMobileTicket}
              language={language}
              giftCertificates={data?.receipt?.giftCertificates}
              ticketsDownloadUri={data?.receipt?.ticketsUri}
              barcodeUri={data?.receipt?.barcodeUri}
              saleId={data?.receipt?.saleId}
              walletUri={data?.receipt?.walletUri}
            />
          </div>
          <div>
            <Divider my="24px" />
            <div className={classes.detailWrapper}>
              <div className={classes.iconWrapper}>
                <BsReceipt />
                <ul className={classes.detail}>
                  <li>
                    <span>{language.Receipt_ReceiptNumber_2}</span>
                  </li>
                  <li>
                    <p className={classes.text}>
                      {data?.receipt.receiptNumber}
                    </p>
                  </li>
                </ul>
              </div>
              <div className={classes.iconWrapper}>
                <FaRegUser />
                <ul className={classes.detail}>
                  <li>
                    <span>{language.Receipt_Customer_Info}</span>
                  </li>
                  <li>
                    <p className={classes.text}>{`${
                      data?.receipt?.customer?.companyName
                        ? data?.receipt?.customer?.companyName
                        : ""
                    }`}</p>
                  </li>
                  <li>
                    <p className={classes.text}>{`${
                      data?.receipt?.customer?.firstName
                        ? data?.receipt?.customer?.firstName
                        : ""
                    } ${
                      data?.receipt?.customer?.lastName
                        ? data?.receipt?.customer?.lastName
                        : ""
                    }`}</p>
                  </li>
                  <li>
                    <p className={classes.text}>
                      {data?.receipt?.customer.email
                        ? data?.receipt?.customer.email
                        : ""}
                    </p>
                  </li>
                </ul>
              </div>
              {state?.deliveryMethod && (
                <div className={classes.iconWrapper}>
                  <BsBoxSeam />
                  <ul className={classes.detail}>
                    <li>
                      <span>{language.Delivery}</span>
                    </li>
                    <li>
                      <p className={classes.text}>-</p>
                    </li>
                  </ul>
                </div>
              )}
              <div className={classes.iconWrapper}>
                <BsCreditCard />
                <ul className={classes.detail}>
                  <li>
                    <span>{language.PaymentMethod}</span>
                  </li>
                  {data?.receipt?.payments.map((el) => (
                    <li key={el.id}>
                      <p className={classes.text}>{el.paymentMethodName}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <Divider my="24px" />
          {useKlarnaReceipt ? (
            <KlarnaReceipt saleId={saleId} />
          ) : (
            <>
              <div>
                <div className={classes.footerWrapper}>
                  <span className={classes.text}>{language.Sum_Excl_Vat}</span>
                  <span className={classes.footerBold}>
                    {data?.receipt?.sumExclVat}
                  </span>
                </div>
                <div className={classes.footerWrapper}>
                  <span className={classes.text}>{language.Sum_Incl_Vat}</span>
                  <span className={classes.footerBold}>
                    {data?.receipt?.sumInclVat}
                  </span>
                </div>

                {fee()}

                {data?.receipt?.discount !== 0 && (
                  <div className={classes.footerWrapper}>
                    <span className={classes.text}>Rabatt</span>
                    <span className={classes.footerBold}>
                      {getCurrencySum(data?.receipt?.discount)}
                    </span>
                  </div>
                )}
              </div>
              <Divider my="24px" />
              <div className={classes.total}>
                <span>{language.Total}</span>
                <span>{data?.receipt?.sumInclVat}</span>
              </div>
            </>
          )}
        </section>
        <OrderList
          config={config}
          language={language}
          articles={data?.receipt?.articles}
        />
      </div>
    </ContentLayout>
  );
};

export default NewReceipt;
